/**
 * ChartDisplay Component
 *
 * Displays chart visualizations generated by the agent
 * Shows chart images (base64 encoded) with type badges and status indicators
 */

import { Box, Typography, Chip, Alert } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ChartGenerated } from "../../contexts/chat/type";

interface ChartDisplayProps {
  charts: ChartGenerated[];
}

export const ChartDisplay = ({ charts }: ChartDisplayProps) => {
  if (!charts || charts.length === 0) {
    return null;
  }

  const getChartIcon = (chartType: string | undefined) => {
    switch (chartType?.toLowerCase()) {
      case "line":
        return <ShowChartIcon sx={{ fontSize: 16 }} />;
      case "bar":
      default:
        return <BarChartIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {charts.map((chart, index) => (
        <Box
          key={index}
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {/* Chart Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: "grey.100",
              borderBottom: "1px solid",
              borderColor: "grey.300",
            }}
          >
            {getChartIcon(chart.chart_type)}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Chart Visualization
            </Typography>
            {chart.chart_type && (
              <Chip
                label={`${chart.chart_type} chart`}
                size="small"
                color="secondary"
                sx={{ height: 20, fontSize: "0.7rem", ml: "auto" }}
              />
            )}
          </Box>

          {/* Chart Content */}
          {chart.status === "success" && chart.chart_url ? (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f9f9f9",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {chart.message && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  {chart.message}
                </Typography>
              )}
              <Box
                component="img"
                src={chart.chart_url}
                alt={`${chart.chart_type || "Chart"} visualization`}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              />
            </Box>
          ) : (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ borderRadius: 0 }}
            >
              <Typography variant="body2">
                {chart.message || "Chart generation failed"}
              </Typography>
            </Alert>
          )}
        </Box>
      ))}
    </Box>
  );
};
